// Commission accounts slice.
//
// Three views of the same data:
//   - `balances`: agent guid -> latest account transaction, treated by the
//     service as that agent's current balance. Keyed by agent, not by record
//     guid, because "what does this agent have?" is the question being asked.
//   - `ledger`: one agent's running account transactions
//   - `crossAgent`: the multi-agent ledger, which is where an operator decides
//     who to pay
//
// `payoutSelection` is the explicit handoff to the batch payout builder. It
// lives here because this page owns the decision; batch payouts only reads it.

import * as types from '../../lib/actionTypes.es6';
import {
  createListReducer,
  initialListState,
  readListResponse
} from '../../components/CommissionsShared/listReducerFactory.es6';

var ledgerList = createListReducer(
  {
    request: types.GET_AGENT_COMMISSION_ACCOUNT_TRANSACTIONS_REQUEST,
    success: types.GET_AGENT_COMMISSION_ACCOUNT_TRANSACTIONS_SUCCESS,
    failure: types.GET_AGENT_COMMISSION_ACCOUNT_TRANSACTIONS_FAILURE
  },
  { keepRowsWhileLoading: true }
);

var crossAgentList = createListReducer(
  {
    request: types.GET_COMMISSION_ACCOUNTS_REQUEST,
    success: types.GET_COMMISSION_ACCOUNTS_SUCCESS,
    failure: types.GET_COMMISSION_ACCOUNTS_FAILURE
  },
  { keepRowsWhileLoading: true }
);

export function initialState() {
  return {
    ledger: initialListState(),
    ledgerAgentGuid: null,
    ledgerParams: {},

    crossAgent: initialListState(),
    crossAgentParams: {},

    // agent guid -> { data, loading, error }
    balances: {},

    // Agent guids carried into the batch payout builder.
    payoutSelection: []
  };
}

function setBalance(state, agentGuid, patch) {
  if (!agentGuid) return state;
  var existing = state.balances[agentGuid] || {
    data: null,
    loading: false,
    error: null
  };
  var updated = {};
  updated[agentGuid] = Object.assign({}, existing, patch);
  return Object.assign({}, state, {
    balances: Object.assign({}, state.balances, updated)
  });
}

// The balance endpoint returns the latest transaction, which may arrive bare or
// wrapped in a list envelope.
function readLatest(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] || null;
  if (Array.isArray(payload.data)) return payload.data[0] || null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
}

export default function commissionAccountsReducer(state, action) {
  if (state === undefined) state = initialState();
  if (!action || !action.type) return state;

  var next = state;

  var ledger = ledgerList(state.ledger, action);
  var crossAgent = crossAgentList(state.crossAgent, action);
  if (ledger !== state.ledger || crossAgent !== state.crossAgent) {
    next = Object.assign({}, next, {
      ledger: ledger,
      crossAgent: crossAgent
    });
  }

  switch (action.type) {
    case types.GET_AGENT_COMMISSION_ACCOUNT_TRANSACTIONS_REQUEST: {
      var request = action.payload || {};
      return Object.assign({}, next, {
        ledgerAgentGuid: request.agentGuid || next.ledgerAgentGuid,
        ledgerParams: request
      });
    }

    case types.GET_COMMISSION_ACCOUNTS_REQUEST:
      return Object.assign({}, next, {
        crossAgentParams: action.payload || {}
      });

    case types.GET_AGENT_COMMISSION_ACCOUNT_REQUEST: {
      var asked = action.payload || {};
      return setBalance(next, asked.agentGuid || asked, {
        loading: true,
        error: null
      });
    }

    case types.GET_AGENT_COMMISSION_ACCOUNT_SUCCESS: {
      // The saga echoes the agent guid back, since the response body does not
      // reliably identify which agent it belongs to.
      var result = action.payload || {};
      return setBalance(next, result.agentGuid, {
        loading: false,
        error: null,
        data: readLatest(result.response)
      });
    }

    case types.GET_AGENT_COMMISSION_ACCOUNT_FAILURE: {
      var failure = action.payload || {};
      // A 404 means this agent has no commission account yet — an ordinary
      // state for a new agent, not an error worth shouting about.
      var missing = failure.error && failure.error.statusCode === 404;
      return setBalance(next, failure.agentGuid, {
        loading: false,
        data: null,
        error: missing ? null : failure.error || { message: 'Unavailable' }
      });
    }

    case types.SET_PAYOUT_AGENT_SELECTION:
      return Object.assign({}, next, {
        payoutSelection: action.payload || []
      });

    case types.CLEAR_PAYOUT_AGENT_SELECTION:
      return Object.assign({}, next, { payoutSelection: [] });

    default:
      return next;
  }
}

// Exported for the tests, which assert the envelope handling directly.
export { readLatest, readListResponse };
